import { TFunction } from "i18next";
import React, { useEffect, useState } from "react";
import styles from "./layout.module.scss";
import {
  TabPanelBuyers,
  TabPanelCompetitors,
  TabPanelInfo,
  TabPanelStatistics,
  TabPanelTenders,
  TabPanelTendersFocus,
} from "./panels";
import { FlagIcon } from "../../components/flags/flags";
import { FollowOrUnfollowCompany } from "../../components/followOrUnfollowCompany";
import { RadarGraph } from "../../components/graphs";
import { CompanyIcon23, LocationIcon, WebsiteIcon } from "../../components/icons";
import { IconWithLink } from "../../components/iconWithLink";
import { IconWithTextAndCopyButton } from "../../components/iconWithTextAndCopyButton";
import { SkeletonSection } from "../../components/skeletonSection";
import { Language } from "../../consts/languages";
import { UserInteractionType } from "../../consts/UserInteractionType";
import {
  BuyersResponse,
  CompanyPage,
  CompetitorsResponse,
  RecentTenderDto,
  SuppliersResponse,
} from "../../hooks/slices/companySlice";
import { LabelTenderDto } from "../../hooks/slices/labelSlice";
import { SearchResponse } from "../../hooks/slices/tenderSearchSlice";
import { UserState } from "../../hooks/slices/userSlice";
import { LabelCategory } from "../../types/label";
import { CompanyUtil } from "../../utils/company";
import { formatNumberShort } from "../../utils/format";
import { TabPanelSuppliers } from "./panels/suppliers";
import { useSearchParams } from "react-router-dom";
import { SearchParamUtil } from "../../utils/searchparams";

export interface LayoutProps {
  stillLoadingContent: boolean;
  isGovernment: boolean;
  pageSizeTenders: number;
  pageSizeCompanies: number;
  t: TFunction;
  user: UserState | null;
  labels: LabelTenderDto[] | null;
  language: Language | null;
  companyUuid: string | null;
  companyIsFollowed: boolean | null;
  companyPageData: CompanyPage | null;

  tendersWonPage: number;
  setTendersWonPage: React.Dispatch<React.SetStateAction<number>>;
  tendersWon: SearchResponse | null;

  tendersParticipatedInPage: number;
  setTendersParticipatedInPage: React.Dispatch<React.SetStateAction<number>>;
  tendersParticipatedIn: SearchResponse | null;

  buyersPage: number;
  setBuyersPage: React.Dispatch<React.SetStateAction<number>>;
  buyers: BuyersResponse | null;

  suppliersPage: number;
  setSuppliersPage: React.Dispatch<React.SetStateAction<number>>;
  suppliers: SuppliersResponse | null;

  competitorsPage: number;
  setCompetitorsPage: React.Dispatch<React.SetStateAction<number>>;
  competitors: CompetitorsResponse | null;

  tendersContractedOpenPage: number;
  setTendersContractedOpenPage: React.Dispatch<React.SetStateAction<number>>;
  tendersContractedOpen: SearchResponse | null;

  tendersContractedClosedPage: number;
  setTendersContractedClosedPage: React.Dispatch<React.SetStateAction<number>>;
  tendersContractedClosed: SearchResponse | null;
}

/**
 * Types
 */
enum MainTabType {
  TENDERING = "TENDERING",
  INFO = "INFO",
  COMPETITORS = "COMPETITORS",
  SUPPLIERS = "SUPPLIERS",
}


function findMainTab(searchParams: URLSearchParams): MainTabType {
  const mainTabParam: string | null = searchParams.get("main");
  const defaultParam: MainTabType = MainTabType.TENDERING;
  if(mainTabParam) {
    const normalizedParam: string = mainTabParam.trim().toUpperCase();
    return Object.values(MainTabType).find(t => t === normalizedParam) ?? defaultParam;
  } else return defaultParam;
}

enum SubTabType {
    TENDERS_WON = "TENDERS_WON",
    TENDERS_PARTICIPATED_IN = "TENDERS_PARTICIPATED_IN",
    BUYERS = "BUYERS",
    TENDERS_FOCUS = "TENDERS_FOCUS",
    TENDERS_CONTRACTED_OPEN = "TENDERS_CONTRACTED_OPEN",
    TENDERS_CONTRACTED_CLOSED = "TENDERS_CONTRACTED_CLOSED",
    STATISTICS = "STATISTICS",
}

function findSubTab(searchParams: URLSearchParams, isGovernment: boolean): SubTabType {
  const subTabParam: string | null = searchParams.get("sub");
  const defaultParam: SubTabType = isGovernment ? SubTabType.TENDERS_CONTRACTED_OPEN : SubTabType.STATISTICS;
  if(subTabParam) {
    const normalizedParam: string = subTabParam.trim().toUpperCase();
    return Object.values(SubTabType).find(t => t === normalizedParam) ?? defaultParam;
  } else return defaultParam;
}

export const Layout: React.FC<LayoutProps> = ({
    stillLoadingContent,
    isGovernment,
    pageSizeTenders,
    pageSizeCompanies,
    t,
    user,
    labels,
    language,
    companyUuid,
    companyIsFollowed,
    companyPageData,
    tendersWonPage,             setTendersWonPage,              tendersWon,
    tendersParticipatedInPage,  setTendersParticipatedInPage,   tendersParticipatedIn,
    buyersPage,                 setBuyersPage,                  buyers,
    suppliersPage,              setSuppliersPage,               suppliers,
    competitorsPage,            setCompetitorsPage,             competitors,
    tendersContractedOpenPage,  setTendersContractedOpenPage,   tendersContractedOpen,
    tendersContractedClosedPage,setTendersContractedClosedPage, tendersContractedClosed,
}) => {
  //Not using useSearchParams hook here because those stick when we navigate to another company page
  const searchParams: URLSearchParams = new URLSearchParams(window.location.search);
  const [activeMainTab, setActiveMainTab] = useState<MainTabType>(findMainTab(searchParams));
  const [activeSubTab, setActiveSubTab] = useState<SubTabType>(findSubTab(searchParams, isGovernment));

  useEffect(() => {
    SearchParamUtil.changeSearchParams(searchParams, "main", activeMainTab);
  }, [activeMainTab]);

  useEffect(() => {
    SearchParamUtil.changeSearchParams(searchParams, "sub", activeSubTab);
  }, [activeSubTab]);

    useEffect(() => {
         setActiveMainTab(findMainTab(searchParams));
         setActiveSubTab(findSubTab(searchParams, isGovernment));
         setTendersWonPage(0);
         setTendersParticipatedInPage(0);
         setBuyersPage(0);
         setCompetitorsPage(0);
         setTendersContractedOpenPage(0);
         setTendersContractedClosedPage(0);
     }, [companyUuid]);

  useEffect(() => {
    //this changes the tab if there are not open contracted tenders for a government
      if(isGovernment && activeMainTab === MainTabType.TENDERING && activeSubTab === SubTabType.TENDERS_CONTRACTED_OPEN) {
        if (tendersContractedOpen != null && tendersContractedClosed != null && tendersContractedOpen.totalHits === 0) {
            if (tendersContractedClosed.totalHits > 0){
              setActiveSubTab(SubTabType.TENDERS_CONTRACTED_CLOSED);
            } else setActiveSubTab(SubTabType.TENDERS_FOCUS);
        }
      }
  }, [tendersContractedOpen, tendersContractedClosed]);


    const userUuid : string | null
        = user?.uuid || null;
    const getLabels : (tenderUuid: string) => LabelTenderDto[]
        = createGetLabelsFunction(labels);
    const getLabelCategories : () => LabelCategory[]
        = createGetLabelCategoriesFunction(user);
    const leads : string[] | null 
        = getLeads(user);
    const deletedOpportunities : string[] | null
        = getDeletedOpportunities(user);
    const companyName : string | null = (companyPageData && language) 
        ? CompanyUtil.getCompanyName(companyPageData, language) 
        : null;

    const RecentlyWon = <TabPanelTenders
        currentPage={tendersWonPage ?? 0}
        totalHits={companyPageData?.tendersWon.length ?? 0}
        itemsPerPage={pageSizeTenders}
        setPage={setTendersWonPage}
        tenders={tendersWon}
        language={language ?? Language.NL}
        leads={leads}
        deletedOpportunities={deletedOpportunities}
        getLabels={getLabels}
        getLabelCategories={getLabelCategories}
    />;

    const RecentlyParticipatedIn = <TabPanelTenders
        currentPage={tendersParticipatedInPage ?? 0}
        totalHits={companyPageData?.tendersParticipatedIn.length ?? 0}
        itemsPerPage={pageSizeTenders}
        setPage={setTendersParticipatedInPage}
        tenders={tendersParticipatedIn}
        language={language ?? Language.NL}
        leads={leads}
        deletedOpportunities={deletedOpportunities}
        getLabels={getLabels}
        getLabelCategories={getLabelCategories}
    />;

    const TendersContractedOpen = <TabPanelTenders
        currentPage={tendersContractedOpenPage ?? 0}
        totalHits={tendersContractedOpen?.totalHits ?? 0}
        itemsPerPage={pageSizeTenders}
        setPage={setTendersContractedOpenPage}
        tenders={tendersContractedOpen}
        language={language ?? Language.NL}
        leads={leads}
        deletedOpportunities={deletedOpportunities}
        getLabels={getLabels}
        getLabelCategories={getLabelCategories}
    />;

    const TendersContractedClosed = <TabPanelTenders
        currentPage={tendersContractedClosedPage ?? 0}
        totalHits={tendersContractedClosed?.totalHits ?? 0}
        itemsPerPage={pageSizeTenders}
        setPage={setTendersContractedClosedPage}
        tenders={tendersContractedClosed}
        language={language ?? Language.NL}
        leads={leads}
        deletedOpportunities={deletedOpportunities}
        getLabels={getLabels}
        getLabelCategories={getLabelCategories}
    />;

    const MainBuyers = <TabPanelBuyers  
        buyers={buyers}
        originalCompanyName={companyName}
        companyType={"contracting authority"}
        currentPage={buyersPage}
        itemsPerPage={pageSizeCompanies}
        setPage={setBuyersPage}
    />

    const Info = <TabPanelInfo 
        companyPage={companyPageData}
        tendersWon={tendersWon}
        tendersParticipatedIn={tendersParticipatedIn}
        isGovernment={isGovernment}
    />;

    const Competitors = <TabPanelCompetitors
        competitors={competitors}
        originalCompanyName={companyName}
        companyType={"company"}
        currentPage={competitorsPage}
        itemsPerPage={pageSizeCompanies}
        setPage={setCompetitorsPage}
    />

    const Suppliers = <TabPanelSuppliers
      suppliers={suppliers}
      originalCompanyName={companyName}
      companyType={"company"}
      currentPage={suppliersPage}
      itemsPerPage={pageSizeCompanies}
      setPage={setSuppliersPage}
    />

    const Focus = <TabPanelTendersFocus
        bids={companyPageData?.bids ?? []}
        t={t}
    />;

    const Statistics = (
        <TabPanelStatistics 
            companyPage={companyPageData}
        />
    );

    const showTenderingSubTabsBar = (companyPageData == null || stillLoadingContent)
    ?   true
    :   entityHasTenderingContent(isGovernment, companyPageData);

    return (
    <div className={styles['page-container']}>
        <div className={styles['header-container']}>
            <div className={styles['header-left-radar']}>
                <HeaderLeftRadarGraph
                    companyPageData={companyPageData}
                    competitors={competitors}
                    suppliers={suppliers}
                    t={t}
                    isGovernment={isGovernment}
                />
            </div>
            <div className={styles['header-left-container']}>
                <HeaderLeftTitle company={companyPageData} isLoading={stillLoadingContent} userUuid={userUuid} companyIsFollowed={companyIsFollowed} language={language ?? Language.NL}/>
                <HeaderLeftInfoList company={companyPageData} isLoading={stillLoadingContent} />
                <div className={styles['header-left-vertical-spacer']}/> </div>
            </div>
        <div className={styles['maintabs-container']} >
            {
                <> {companyHasTenderingContent(companyPageData) &&
                  <MainTabTendering t={t} isLoading={stillLoadingContent} isActive={ activeMainTab === MainTabType.TENDERING } onClick={() => setActiveMainTab(MainTabType.TENDERING)}/>}
                  <MainTabsCompanyInformation t={t} isLoading={stillLoadingContent} isActive={ activeMainTab === MainTabType.INFO } onClick={() => setActiveMainTab(MainTabType.INFO)}/>
                    {!isGovernment && <MainTabCompetitors t={t} isLoading={stillLoadingContent} numberOfElements={competitors?.totalHits ?? null} isActive={ activeMainTab === MainTabType.COMPETITORS } onClick={() => setActiveMainTab(MainTabType.COMPETITORS)}/> }
                    {isGovernment && <MainTabSuppliers t={t} isLoading={stillLoadingContent} numberOfElements={suppliers?.totalHits ?? null} isActive={ activeMainTab === MainTabType.SUPPLIERS } onClick={() => setActiveMainTab(MainTabType.SUPPLIERS)}/> }
                </>
            } </div>
        {activeMainTab === MainTabType.TENDERING && showTenderingSubTabsBar &&
          <div className={styles['subtabs-container']}>
            <div className={styles['subtab-bar']}>
            { isGovernment
                ? <>
                    <SubTabTendersContractedOpen t={t} isLoading={stillLoadingContent} numberOfElements={tendersContractedOpen?.totalHits ?? null} isActive={activeSubTab === SubTabType.TENDERS_CONTRACTED_OPEN} onClick={() => setActiveSubTab(SubTabType.TENDERS_CONTRACTED_OPEN)}/>
                    <SubTabTendersContractedClosed t={t} isLoading={stillLoadingContent} numberOfElements={tendersContractedClosed?.totalHits ?? null} isActive={activeSubTab === SubTabType.TENDERS_CONTRACTED_CLOSED} onClick={() => setActiveSubTab(SubTabType.TENDERS_CONTRACTED_CLOSED)}/>
                    <SubTabTendersFocus t={t} isLoading={stillLoadingContent} numberOfElements={companyPageData?.subjectCodes.length ?? null} isActive={activeSubTab === SubTabType.TENDERS_FOCUS} onClick={() => setActiveSubTab(SubTabType.TENDERS_FOCUS)}/>
                </>
                : <>
                    <SubTabStatistics t={t} isLoading={stillLoadingContent} numberOfElements={null} isActive={activeSubTab === SubTabType.STATISTICS} onClick={() => setActiveSubTab(SubTabType.STATISTICS)} />
                    <SubTabTendersWon t={t} isLoading={stillLoadingContent} numberOfElements={companyPageData?.tendersWon.length ?? null} isActive={activeSubTab === SubTabType.TENDERS_WON} onClick={() => setActiveSubTab(SubTabType.TENDERS_WON)}/>
                    <SubTabTendersParticipatedIn t={t} isLoading={stillLoadingContent} numberOfElements={companyPageData?.tendersParticipatedIn.length ?? null} isActive={activeSubTab === SubTabType.TENDERS_PARTICIPATED_IN} onClick={() => setActiveSubTab(SubTabType.TENDERS_PARTICIPATED_IN)}/>
                    <SubTabBuyer t={t} isLoading={stillLoadingContent} numberOfElements={buyers?.totalHits ?? null} isActive={activeSubTab === SubTabType.BUYERS} onClick={() => setActiveSubTab(SubTabType.BUYERS)}/>
                    <SubTabTendersFocus t={t} isLoading={stillLoadingContent} numberOfElements={companyPageData?.subjectCodes.length ?? null} isActive={activeSubTab === SubTabType.TENDERS_FOCUS} onClick={() => setActiveSubTab(SubTabType.TENDERS_FOCUS)}/>
                </>
            }</div>
          </div>
        }
      {!stillLoadingContent &&
        <div className={styles['tabpanel-container']}>
          { isGovernment
            ? <> { (activeMainTab === MainTabType.TENDERING) && <>
              {(activeSubTab === SubTabType.TENDERS_CONTRACTED_OPEN) && TendersContractedOpen}
              {(activeSubTab === SubTabType.TENDERS_CONTRACTED_CLOSED) && TendersContractedClosed}
              {(activeSubTab === SubTabType.TENDERS_FOCUS) && Focus}
            </>}
              { (activeMainTab === MainTabType.INFO) && Info}
              { (activeMainTab === MainTabType.SUPPLIERS) && Suppliers}
            </>
            : <> { (activeMainTab === MainTabType.TENDERING) && <>
              {(activeSubTab === SubTabType.TENDERS_WON) && RecentlyWon}
              {(activeSubTab === SubTabType.TENDERS_PARTICIPATED_IN) && RecentlyParticipatedIn}
              {(activeSubTab === SubTabType.BUYERS) && MainBuyers}
              {(activeSubTab === SubTabType.TENDERS_FOCUS) && Focus}
              {(activeSubTab === SubTabType.STATISTICS) && Statistics}
            </>}
              { (activeMainTab === MainTabType.INFO) && Info}
              { (activeMainTab === MainTabType.COMPETITORS) && Competitors}
            </>
          }</div>
      }
    </div>);
};


/**
 * Skeletons
 */
const SkeletonProgressCircle: React.FC = () => {
    return (<>
        <SkeletonSection width="100px" height={"100px"} margin={'0'} borderRadius={'50%'}/>
    </>)
}

const SkeletonMainTab: React.FC<{label:string}> = ({
    label,
}) => {
    return (<div className={styles['maintab']}>
        <SkeletonSection width="64px" height={'10px'} margin={'5px 0'} />
    </div>);
}

const SkeletonSubTab: React.FC = ({}) => {
    return (<div className={styles['subtab']}>
        <SkeletonSection width="96px" height={'20px'} margin={'0 0'} />
    </div>);
}

/**
 * Subcomponents
 */
interface HeaderLeftTitleProps {
    company : CompanyPage | null;
    userUuid : string | null;
    companyIsFollowed : boolean | null;
    language: Language;
    isLoading: boolean
}
const HeaderLeftTitle : React.FC<HeaderLeftTitleProps> = ({ 
    company,
    userUuid,
    companyIsFollowed = false,
    language,
    isLoading
}) => {
    let companyName = CompanyUtil.getCompanyName(company, language);
    return (<>{ (!isLoading && company !== null)
    ?   <div className={styles['header-left-title']}>
            <div className={styles['header-left-title-flag']}>
                <FlagIcon country={CompanyUtil.getCompanyCountry(company)}/></div>
            <div className={styles['header-left-title-name']}>
                {companyName}   </div>
            <div className={styles['header-left-title-legalform']}>
                {company.juridicalForm ? ` (${company.juridicalForm})` : ' '}</div>
            <div className={styles['header-left-title-followbutton']}>
                <FollowOrUnfollowCompany
                    userUuid={userUuid ?? ""}
                    companyIsFollowed={companyIsFollowed ?? false}
                    company={company}/> </div> </div>
    :   <SkeletonSection width="50%"height={20} margin={'0'} />
    }</>);
};

interface HeaderLeftInfoListProps {
    company : CompanyPage | null;
    isLoading: boolean;
}
const HeaderLeftInfoList: React.FC<HeaderLeftInfoListProps> = ({ 
    company, isLoading
}) => {
    let address = "";
    if (company?.street) address += company.street;
    if (company?.postalCode) address += ", " + company.postalCode;
    if (company?.city) address += " " + company.city;
    return (<>
        {(!isLoading && company)
        ?   <div className={styles['header-left-info-list']}>
                <IconWithTextAndCopyButton 
                    icon={<CompanyIcon23/>} 
                    text={company.vatNumber.replace("BE", "")}/>
                {(address !== "") && <IconWithTextAndCopyButton 
                    icon={<LocationIcon/>} 
                    text={address}/>}
                {company.website && <IconWithLink 
                    icon={<WebsiteIcon/>} 
                    text={company.website}/>}
            </div>
        :   <div className={styles['header-left-info-list']}>
                { [...Array(3)].map((_, i) => 
                    <SkeletonSection 
                        key={i}
                        width="25%"
                        height={10}
                        margin={'5px 0'}  
                    />
                )}
            </div>
        }
    </>);
};

interface HeaderLeftRadarGraphProps {   
    companyPageData : CompanyPage | null;
    competitors : CompetitorsResponse | null;
    suppliers : SuppliersResponse | null;
    t: TFunction;
    isGovernment: boolean;
}
const HeaderLeftRadarGraph: React.FC<HeaderLeftRadarGraphProps> = ({
    companyPageData,
    competitors,
    suppliers,
    t,
    isGovernment,
}) => {
    const priceFocusPercentage = companyPageData?.percentageOfLotsThatOnlyDependOnPrice ?? 0;
    const winRatePercentage = companyPageData?.percentageOfBidsWon ?? 0;
    const experienceAmount= (isGovernment) 
    ?   companyPageData?.tendersContracted.length ?? 0
    :   companyPageData?.tendersWon.length ?? 0; 
    const qualityFocusPercentage = companyPageData?.percentageOfLotsThatOnlyDependOnPrice ? 100 - companyPageData.percentageOfLotsThatOnlyDependOnPrice : 0;
    const averageLotValue = companyPageData?.averageBidValue ?? 0;
    const competitorsAmount = competitors?.totalHits ?? 0;
    const suppliersAmount = suppliers?.totalHits ?? 0;

    if(isGovernment) return <>
        <RadarGraph
            data={[
                {   subject: t("companyPage.price"),
                    scaledValue: {value: priceFocusPercentage, min: 0, max: 100, scaleType: 'linear'},
                    label:`${Math.round(priceFocusPercentage)}%`,
                },
                {   subject: t("companyPage.company_experience_title"),
                    scaledValue: (isGovernment)
                    ? {value: experienceAmount, min: 0, max: 500, scaleType: 'linear'}
                    : {value: experienceAmount, min: 0, max: 50, scaleType: 'linear'},
                    label:`${experienceAmount} ${t('companyPage.tenders_plural')}`,
                },
                {   subject: t("companyPage.quality"),
                    scaledValue: {value: qualityFocusPercentage, min: 0, max: 100, scaleType: 'linear'},
                    label:`${Math.round(qualityFocusPercentage)}%`,
                },
                {   subject: t("companyPage.averageLotValue"),
                    scaledValue: {value: averageLotValue, min: 1, max: 100_000_000, scaleType: 'log'},
                    label: formatNumberShort(averageLotValue, { suffix: ' €', invalidInputReturn: '0 €' }) ?? undefined,
                },
                {   subject: t("companyPage.suppliers"),
                    scaledValue: {value: suppliersAmount, min: 1, max: 100, scaleType: 'log'},
                    label:`${suppliersAmount} ${t('companyPage.suppliers')}`,
                },
            ]}
        />
    </>;
    else return <>
      <RadarGraph
        data={[
          {   subject: t("companyPage.price"),
            scaledValue: {value: priceFocusPercentage, min: 0, max: 100, scaleType: 'linear'},
            label:`${Math.round(priceFocusPercentage)}%`,
          },
          {   subject: t("companyPage.win_ratio_title_1"),
            scaledValue: {value: winRatePercentage, min: 0, max: 100, scaleType: 'linear'},
            label:`${Math.round(winRatePercentage)}%`,
          },
          {   subject: t("companyPage.company_experience_title"),
            scaledValue: (isGovernment)
              ? {value: experienceAmount, min: 0, max: 500, scaleType: 'linear'}
              : {value: experienceAmount, min: 0, max: 50, scaleType: 'linear'},
            label:`${experienceAmount} ${t('companyPage.tenders_plural')}`,
          },
          {   subject: t("companyPage.quality"),
            scaledValue: {value: qualityFocusPercentage, min: 0, max: 100, scaleType: 'linear'},
            label:`${Math.round(qualityFocusPercentage)}%`,
          },
          {   subject: t("companyPage.averageLotValue"),
            scaledValue: {value: averageLotValue, min: 1, max: 100_000_000, scaleType: 'log'},
            label: formatNumberShort(averageLotValue, { suffix: ' €', invalidInputReturn: '0 €' }) ?? undefined,
          },
          {   subject: t("companyPage.competitors"),
            scaledValue: {value: competitorsAmount, min: 1, max: 100, scaleType: 'log'},
            label:`${competitorsAmount} ${t('companyPage.competitors')}`,
          },
        ]}
      />
    </>;
}

/**
 * MainTabs
 */
interface MainTabProps {
    t: TFunction;
    isLoading : boolean;
    isActive: boolean;onClick: () => void
}

const MainTabTendering: React.FC<MainTabProps> = ({t, isLoading, isActive, onClick,
}) => {
    return (<>{ (!isLoading)
    ?   <div    className={`${styles['maintab']} ${isActive ? styles['active'] : ''}`}
                onClick={onClick}
        >
            {t("companyPage.tendering")} </div>
    :   <SkeletonMainTab label={t("companyPage.tendering")} />
    }</>);
}

const MainTabsCompanyInformation: React.FC<MainTabProps> = ({
    t, isLoading,
    isActive,
    onClick,
}) => {
    return (<>{ (!isLoading)
    ?   <div className={`${styles['maintab']} ${isActive ? styles['active'] : ''}`}
                onClick={onClick}>
            {t("companyPage.company_information_section_title")} </div>
    :   <SkeletonMainTab label={t("companyPage.company_information_section_title")} />
    }</>);
}

interface MainTabCompetitorsProps {
    t: TFunction;
    numberOfElements : number | null;
    isActive: boolean;
    onClick: () => void,
    isLoading: boolean
}

const MainTabCompetitors: React.FC<MainTabCompetitorsProps> = ({
    t,
    numberOfElements,
    isActive,
    onClick,
    isLoading
}) => {
    return (<>{ (!isLoading && numberOfElements !== null)
    ?   (numberOfElements>0)
        ?   <div className={`${styles['maintab']} ${isActive ? styles['active'] : ''}`}
                onClick={onClick}>
               {`  ${t("companyPage.competitors")}  
                    (${numberOfElements})
                `} </div>
        :   null
    :   <SkeletonMainTab label={t("companyPage.competitors")} />
    }</>);
}

const MainTabSuppliers: React.FC<MainTabCompetitorsProps> = ({
                                                                 t,
                                                                 numberOfElements,
                                                                 isActive,
                                                                 onClick,
                                                                 isLoading
                                                               }) => {
  return (<>{ (!isLoading && numberOfElements !== null)
    ?   (numberOfElements>0)
      ?   <div className={`${styles['maintab']} ${isActive ? styles['active'] : ''}`}
               onClick={onClick}>
        {`  ${t("companyPage.suppliers")}  
                    (${numberOfElements})
                `} </div>
      :   null
    :   <SkeletonMainTab label={t("companyPage.suppliers")} />
  }</>);
}

/**
 * SubTabs
 */
interface SubTabProps {
    t: TFunction;
    numberOfElements : number | null;
    isActive: boolean;
    onClick: () => void;
    isLoading: boolean;
}

const SubTabTendersWon: React.FC<SubTabProps> = ({
    t,
    numberOfElements,
    isActive,
    onClick,
    isLoading
}) => {
    return (<>{ (!isLoading && numberOfElements !== null)
    ?   (numberOfElements > 0)
        ?   <div className={`${styles['subtab']} ${isActive ? styles['active'] : ''}`}
                onClick={onClick}>
                {`  ${t("companyPage.recently_won_section_title")}
                    (${numberOfElements})
            `} </div>
        :   null
    :   <SkeletonSubTab />
    }</>);
}

const SubTabTendersParticipatedIn: React.FC<SubTabProps> = ({
    t,
    numberOfElements,
    isActive,
    onClick,
    isLoading
}) => {
    return (<>{ (!isLoading && numberOfElements !== null)
    ?   (numberOfElements > 0)
        ?   <div className={`${styles['subtab']} ${isActive ? styles['active'] : ''}`}
                onClick={onClick}>
                {`  ${t("companyPage.recently_participated_in_section_title")}
                    (${numberOfElements})
                `} </div>
        :   null
    :   <SkeletonSubTab />
    }</>);
}

const SubTabBuyer: React.FC<SubTabProps> = ({
    t,
    numberOfElements,
    isActive,
    onClick,
    isLoading
}) => {
    return (<>{ (!isLoading && numberOfElements !== null)
    ?   (numberOfElements   > 0)
        ?   <div className={`${styles['subtab']} ${isActive ? styles['active'] : ''}`}
                onClick={onClick}>
                {`  ${t("companyPage.main_buyers_section_title")}
                `} </div>
        :   null
    :   <SkeletonSubTab />
    }</>);
}

const SubTabTendersFocus: React.FC<SubTabProps> = ({
    t,
    numberOfElements,
    isActive,
    onClick,
    isLoading
}) => {
    return (<>{ (!isLoading && numberOfElements !== null)
    ?   (numberOfElements > 0)
        ?   <div className={`${styles['subtab']} ${isActive ? styles['active'] : ''}`}
                onClick={onClick}>
                {`  ${t("companyPage.main_activities_section_title")}
                `} </div>
        :   null
    :   <SkeletonSubTab />
    }</>);
}

const SubTabTendersContractedOpen: React.FC<SubTabProps> = ({
    t,
    numberOfElements,
    isActive,
    onClick,
    isLoading
}) => {
    return (<>{ (!isLoading && numberOfElements !== null)
    ?   (numberOfElements > 0)
        ?   <div className={`${styles['subtab']} ${isActive ? styles['active'] : ''}`}
                onClick={onClick}>
                {`  ${t("companyPage.contracted_open_section_title")}
                    (${numberOfElements})
                `} </div>
        :   null
    :   <SkeletonSubTab />
    }</>);
}

const SubTabTendersContractedClosed: React.FC<SubTabProps> = ({
    t,
    numberOfElements,
    isActive,
    onClick,
    isLoading
}) => {
    return (<>{ (!isLoading && numberOfElements !== null)
    ?   (numberOfElements>0)
        ?   <div className={`${styles['subtab']} ${isActive ? styles['active'] : ''}`}
                onClick={onClick}>
                {`  ${t("companyPage.contracted_closed_section_title")}
                    (${numberOfElements})
                `} </div>
        :   null
    :   <SkeletonSubTab />
    }</>);

}

const SubTabStatistics: React.FC<SubTabProps> = ({
    t,
    numberOfElements,
    isActive,
    onClick,
    isLoading
}) => {
    const displayCount = numberOfElements !== null ? `(${numberOfElements})` : '';
    return (!isLoading ?
        <div className={`${styles['subtab']} ${isActive ? styles['active'] : ''}`}
            onClick={onClick}>
            {`${t('companyPage.statistics_tab_title')} ${displayCount}`}
        </div>
        : <SkeletonSubTab />
    );
}

/**
 * Utils
 */
const getTendersLostMinimal = (
    tendersParticipatedInMinimal: RecentTenderDto[] | null,
    tendersWonMinimal: RecentTenderDto[] | null,
) : RecentTenderDto[] | null => {
    if (tendersParticipatedInMinimal && tendersWonMinimal) {
        return tendersParticipatedInMinimal
            .filter(t => t.type === "CONTRACT_AWARD")
            .filter(t => !tendersWonMinimal.map(t => t.uuid).includes(t.uuid));
    } else {
        return null;
    }
}

const getAllTendersContractedMinimal = function(
    tendersContractedOpenMinimal: RecentTenderDto[] | null,
    tendersContractedClosedMinimal: RecentTenderDto[] | null,
){
    if (tendersContractedOpenMinimal && tendersContractedClosedMinimal) {
        return [...tendersContractedOpenMinimal, ...tendersContractedClosedMinimal];
    } else if (tendersContractedOpenMinimal) {
        return tendersContractedOpenMinimal;
    } else if (tendersContractedClosedMinimal) {
        return tendersContractedClosedMinimal;
    } else {
        return null;
    }
};

const getLeads = (
    user: UserState | null,
) : string[] | null => {
    return user?.userInteractions?.filter(
        i => i.interactionType === UserInteractionType.MAKE_LEAD)
        .map(i => i.uuid) ?? null;
}

const getDeletedOpportunities = (
    user : UserState | null,
) : string[] | null => {
    return user?.userInteractions?.filter(
        i => i.interactionType === UserInteractionType.DELETE_OPPORTUNITY)
        .map(i => i.uuid) ?? null;
}

const createGetLabelsFunction = (
    labels: LabelTenderDto[] | null
) :  (tenderUuid: string)  => LabelTenderDto[] => {
    if (labels === null) return (tenderUuid: string) => [];
    const getLabels = (
        tenderUuid: string,
    ): LabelTenderDto[] => {
        return labels.filter(l => l.tenderUuid === tenderUuid);
    }
    return getLabels;
}

const createGetLabelCategoriesFunction = (
    user: UserState | null,
) : () => LabelCategory[] => {
    if (user === null) return () => [];
    const getLabelCategories = (): LabelCategory[] => {
        return (user.labelCategories !== undefined) 
            ? user.labelCategories 
            : []
        ;
    }
    return getLabelCategories;
}

const entityHasTenderingContent = (
    isGovernment: boolean,
    companyPageData: CompanyPage,
): boolean => {
    return isGovernment
        ? governmentHasTenderingContent(companyPageData)
        : companyHasTenderingContent(companyPageData);
}

const companyHasTenderingContent = (
    companyPageData: CompanyPage | null,
): boolean => {
    if (companyPageData === null) return false;
    return companyPageData.tendersWon.length > 0
        || companyPageData.tendersParticipatedIn.length > 0
        || companyPageData.subjectCodes.length > 0;
}

const governmentHasTenderingContent = (
    companyPageData: CompanyPage | null,
): boolean => {
    if (companyPageData === null) return false;
    return companyPageData.tendersContracted.length > 0
        || companyPageData.subjectCodes.length > 0;
}